import React, { useState } from 'react';
import { Heart, ImageOff } from 'lucide-react';
import { Button } from '../ui/button';

interface Product {
  id: string;
  name: string;
  brand: string;
  price: number;
  compareAtPrice?: number | null;
  imageUrl: string;
  sizes?: Array<{ label: string; available: boolean }>;
  isNew?: boolean;
}

interface ProductCardProps {
  product: Product;
  onAddToCart: (size?: string, e?: React.MouseEvent) => void;
  onFavorite: (id: string) => void;
  isFavorited?: boolean;
  compact?: boolean;
}

export function ProductCard({ product, onAddToCart, onFavorite, isFavorited, compact }: ProductCardProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const [isSizePickerOpen, setIsSizePickerOpen] = useState(false);

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (product.sizes?.length) {
      setIsSizePickerOpen(true);
      return;
    }
    onAddToCart(undefined, e);
  };

  const handleSelectSize = (size: string, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsSizePickerOpen(false);
    onAddToCart(size, e);
  };

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onFavorite(product.id);
  };

  const hasImage = Boolean(product.imageUrl) && !imgFailed;

  return (
    <div className="group relative">
      <div className={`aspect-[3/4] w-full overflow-hidden bg-white border border-gray-200 ${compact ? 'rounded-xl' : 'rounded-2xl'}`}>
        {hasImage ? (
          <img
            src={product.imageUrl}
            alt={product.name}
            className="h-full w-full object-cover object-center transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center bg-gray-100 text-gray-400">
            <ImageOff className={compact ? 'h-8 w-8' : 'h-10 w-10'} />
            <span className={`mt-2 font-medium ${compact ? 'text-[10px]' : 'text-xs'}`}>No image</span>
          </div>
        )}
        
        {/* Sale Badge */}
        {product.compareAtPrice && product.compareAtPrice > product.price && (
          <span className={`absolute left-2 bg-red-500 text-white font-bold uppercase tracking-wider rounded-full ${compact ? 'top-2 text-[9px] px-1.5 py-0.5' : 'top-3 text-[10px] px-2 py-0.5'}`}>
            {Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)}% off
          </span>
        )}

        {/* Favorite Button Overlay */}
        <button
          onClick={handleFavorite}
          className={`absolute right-2 rounded-full bg-white/80 backdrop-blur-sm transition-colors hover:bg-white ${compact ? 'top-2 p-1.5' : 'top-3 p-2'}`}
        >
          <Heart className={`${compact ? 'h-4 w-4' : 'h-5 w-5'} transition-colors ${isFavorited ? 'fill-primary text-primary' : ''}`} />
        </button>

        {/* Quick Add Overlay (Desktop) */}
        <div className={`absolute inset-x-4 bottom-4 transition-all duration-300 hidden md:block ${
          isSizePickerOpen
            ? 'translate-y-0 opacity-100'
            : 'translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100'
        }`}>
          {isSizePickerOpen ? (
            <div
              className="rounded-xl bg-white/95 p-3 shadow-lg backdrop-blur-md"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
              }}
            >
              <p className="mb-2 text-center text-xs font-medium text-heading">Select a size</p>
              <div className="flex flex-wrap justify-center gap-1.5">
                {product.sizes?.map((size) => (
                  <button
                    key={size.label}
                    type="button"
                    disabled={!size.available}
                    onClick={(e) => handleSelectSize(size.label, e)}
                    className="min-w-9 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-heading transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-300 disabled:line-through"
                  >
                    {size.label}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <Button
              onClick={handleQuickAdd}
              fullWidth
              className="bg-white/90 text-heading backdrop-blur-md hover:bg-white shadow-lg"
            >
              Quick Add
            </Button>
          )}
        </div>
      </div>
      
      <div className={`flex justify-between ${compact ? 'mt-2' : 'mt-4'}`}>
        <div className="min-w-0 flex-1 pr-2">
          <p className={`font-medium text-primary uppercase tracking-wider ${compact ? 'text-[10px] mb-0.5' : 'text-xs mb-1'}`}>
            {product.brand}
          </p>
          <h3 className={`font-serif font-medium text-heading truncate ${compact ? 'text-xs' : 'text-sm'}`}>
            {product.name}
          </h3>
        </div>
        <div className="flex-shrink-0 text-right">
          {product.compareAtPrice && product.compareAtPrice > product.price ? (
            <>
              <p className={`text-gray-400 line-through ${compact ? 'text-[10px]' : 'text-xs'}`}>
                ${compact ? product.compareAtPrice : product.compareAtPrice.toFixed(2)}
              </p>
              <p className={`font-medium text-red-600 ${compact ? 'text-xs' : 'text-sm'}`}>
                ${compact ? product.price : product.price.toFixed(2)}
              </p>
            </>
          ) : (
            <p className={`font-medium text-heading ${compact ? 'text-xs' : 'text-sm'}`}>
              ${compact ? product.price : product.price.toFixed(2)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
